import React, { useState } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, CheckCircle2, FileSpreadsheet, FileText } from "lucide-react";
import { downloadBlob } from "@/lib/download";

export default function ImportComplaints() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [defaultStatus, setDefaultStatus] = useState("New Complaint");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const doPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("access_token");
      const r = await fetch(`${API}/import/preview`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(formatApiErrorDetail(j.detail) || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setPreview(data);
      setMapping(data.suggested_mapping || {});
    } catch (e) {
      toast.error("Preview failed", { description: e.message });
    } finally { setLoading(false); }
  };

  const doImport = async () => {
    if (!file || !preview) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("default_status", defaultStatus);
      const token = localStorage.getItem("access_token");
      const r = await fetch(`${API}/import/complaints`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await r.json();
      if (!r.ok) throw new Error(formatApiErrorDetail(data.detail) || `HTTP ${r.status}`);
      setResult(data);
      toast.success("Import complete", { description: `${data.inserted} inserted, ${data.skipped} skipped` });
    } catch (e) {
      toast.error("Import failed", { description: e.message });
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="import-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Import Complaints</h1>
          <p className="text-sm text-slate-500 mt-1">Upload a CSV or Excel file (e.g., your existing Google Sheet export), map columns to CRM fields, then import.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => downloadBlob({ url: "/import/template/complaints.xlsx", filename: "complaints_import_template.xlsx" })}
                data-testid="template-complaints-btn"
                className="border-amber-300 text-amber-700 hover:bg-amber-50">
          <FileText size={13} className="mr-1" /> Download Template
        </Button>
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <Label>File (.csv, .xlsx)</Label>
              <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }} data-testid="import-file-input" />
            </div>
            <Button onClick={doPreview} disabled={!file || loading} className="bg-slate-900 text-white" data-testid="preview-btn">
              <Upload size={14} className="mr-1" /> {loading ? "Reading..." : "Preview & Map"}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2"><FileSpreadsheet size={12} className="inline mr-1" /> Tip: export your Google Sheet as .xlsx or .csv first.</p>
        </CardContent>
      </Card>

      {preview && (
        <>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="font-heading font-semibold text-slate-900">Column Mapping</div>
                <div className="text-xs text-slate-500">{preview.total_rows} rows detected · {preview.headers.length} columns</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {preview.headers.map((h) => (
                  <div key={h} className="flex items-center gap-3" data-testid={`map-row-${h}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 truncate">{h}</div>
                      <div className="text-[10px] text-slate-400 truncate">e.g. {preview.preview_rows?.[0]?.[preview.headers.indexOf(h)] || "—"}</div>
                    </div>
                    <Select value={mapping[h] || "__skip__"} onValueChange={(v) => setMapping({ ...mapping, [h]: v })}>
                      <SelectTrigger className="w-64 h-8 text-xs" data-testid={`map-select-${h}`}>
                        <SelectValue placeholder="Skip" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="__skip__">— Skip this column —</SelectItem>
                        {preview.crm_fields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="border-t mt-4 pt-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Label className="m-0 text-xs">Default Status</Label>
                  <Select value={defaultStatus} onValueChange={setDefaultStatus}>
                    <SelectTrigger className="h-8 w-48 text-xs" data-testid="default-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["New Complaint","Pending","Closed","Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={doImport} disabled={loading} className="bg-slate-900 text-white" data-testid="import-btn">
                  {loading ? "Importing..." : `Import ${preview.total_rows} rows`}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card><CardContent className="p-0">
            <div className="p-3 border-b font-heading font-semibold text-slate-900">Data Preview (first 10 rows)</div>
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full dense-table">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr>{preview.headers.map((h) => <th key={h} className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {preview.preview_rows.map((row, ri) => (
                    <tr key={ri}>{row.map((cell, ci) => <td key={ci} className="text-xs text-slate-700 max-w-xs truncate">{cell}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </>
      )}

      {result && (
        <Card data-testid="import-result"><CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={18} className="text-emerald-600" />
            <div className="font-heading font-semibold text-slate-900">Import Complete</div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="border rounded-md p-3"><div className="text-2xl font-heading font-bold text-emerald-700">{result.inserted}</div><div className="text-xs uppercase tracking-wide text-slate-500">Inserted</div></div>
            <div className="border rounded-md p-3"><div className="text-2xl font-heading font-bold text-amber-700">{result.skipped}</div><div className="text-xs uppercase tracking-wide text-slate-500">Skipped</div></div>
            <div className="border rounded-md p-3"><div className="text-2xl font-heading font-bold text-rose-700">{result.errors?.length || 0}</div><div className="text-xs uppercase tracking-wide text-slate-500">Errors</div></div>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-3 text-xs space-y-1">
              {result.errors.map((e, i) => <div key={i} className="text-rose-700">Row {e.row}: {e.error}</div>)}
            </div>
          )}
        </CardContent></Card>
      )}
    </div>
  );
}
